import { useMemo, useCallback } from "react";
import { Flex, Text } from "@mantine/core";
import StickyOwnerFilter from "./StickyOwnerFilter";
import StickyProductAreaFilter from "./StickyProductAreaFilter";
import StickyReleaseFilter from "./StickyReleaseFilter";
import ValidatedSwitch from "./ValidatedSwitch";
import CreatedAtFiltersIdeas from "../../created-at-filters/CreatedAtFiltersIdeas";
import AppliedFilterChip from "./AppliedFilterChip";
import { useFiltersContextHelper } from "../../filters/useFiltersContextHelper";
import { areFiltersEquivalent } from "../../../evidences/utils/filterComparison";

// Filters with their own always-visible trigger, so they must not also show
// up as an applied-filter chip.
const STICKY_FILTER_KEYS = new Set(['owner', 'product-area', 'release']);

interface StickyFiltersProps {
  subPageKey?: string;
  setFilterModalOpened: (opened: boolean) => void;
  selectedView?: any;
}

const StickyFilters = ({ subPageKey, setFilterModalOpened, selectedView }: StickyFiltersProps) => {
  const { activeFilters, setActiveFilters } = useFiltersContextHelper();
  const viewFilters: any[] | undefined = selectedView?.filters;

  const chipFilters = useMemo(() => {
    if (!activeFilters || !Array.isArray(activeFilters)) return [];

    return activeFilters.filter((filter: any) => {
      // Skip invalid
      const hasSavedKey = !!(filter as any).savedKey;
      if (filter.selected === undefined && !hasSavedKey) return false;
      if (Array.isArray(filter.selected) && filter.selected.length === 0 && !hasSavedKey) return false;

      // Skip sticky filter keys
      const name = (filter.name || '').toLowerCase();
      const parent = (filter.parent || '').toLowerCase();
      if (STICKY_FILTER_KEYS.has(name) || STICKY_FILTER_KEYS.has(parent)) return false;

      // Skip created-at date filter (evidence date)
      if ((filter.type === 'date' || filter.type === 'date-range') && filter.filterObject === 'evidence') return false;

      // Skip view-applied filters
      if (viewFilters && Array.isArray(viewFilters) && viewFilters.length > 0) {
        const isFromView = viewFilters.some((vf: any) => areFiltersEquivalent(filter, vf));
        if (isFromView) return false;
      }

      // Skip hidden badge filters
      if (filter.hiddenBadge) return false;

      return true;
    });
  }, [activeFilters, viewFilters]);

  const handleRemoveFilter = useCallback((filterName: string) => {
    setActiveFilters((prev: any[]) => {
      return (prev || []).filter((f: any) => f.name !== filterName);
    });
  }, [setActiveFilters]);

  const handleUpdateFilter = useCallback((filterName: string, newSelected: string[]) => {
    if (newSelected.length === 0) {
      handleRemoveFilter(filterName);
      return;
    }
    setActiveFilters((prev: any[]) => {
      return (prev || []).map((f: any) =>
        f.name === filterName ? { ...f, selected: newSelected } : f
      );
    });
  }, [setActiveFilters, handleRemoveFilter]);

  return (
    <Flex gap="sm" align="center">
      <StickyOwnerFilter subPageKey={ subPageKey }/>
      <StickyProductAreaFilter subPageKey={ subPageKey }/>
      <StickyReleaseFilter subPageKey={ subPageKey }/>
      <CreatedAtFiltersIdeas subPageKey={ subPageKey }/>
      <ValidatedSwitch />
      {chipFilters.map((filter: any) => (
        <AppliedFilterChip
          key={filter.name}
          filter={filter}
          onRemove={handleRemoveFilter}
          onUpdateSelected={handleUpdateFilter}
        />
      ))}
      <Text
        fz={12}
        fw={500}
        onClick={() => setFilterModalOpened(true)}
        sx={{
          color: '#D1D5DB',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          ':hover': {
            color: '#6B7280',
          },
        }}
      >
        + Add filter
      </Text>
    </Flex>
  );
}

export default StickyFilters
